/**
 * Items v2 (docs/systems/ITEMS_V2.md §1.4–1.5, §4.1, §5.1): the random affixes a dropped or crafted piece rolls, as data,
 * and the pure roller.
 *
 * - **Item level** (`ilv`, 1–60) picks the value band: each affix has a `[lo, hi]` at ilv 1 and at ilv 60, and the
 *   band steps linearly every 10 levels (1–10, 11–20 … 51–60).
 * - **Quality** (`q`, 0–100 %) is where inside the band a roll landed; the tooltip shows it as pips.
 * - **Rarity** sets how many affixes roll (`AFFIX_COUNT`); a rarity's **budget** (`itemBudget`) caps their sum. An
 *   affix at the top of its band costs 1 unit; when an item rolls more affixes than its budget has units, each one is
 *   scaled down by the same share, so a 6-affix ไร้เทียมทาน is wider, not 50 % stronger, than a 3-affix หายาก.
 * - **Groups:** one affix per `group` per item (no double crit). **Slots:** each affix lists the slots it may roll
 *   on; `SLOT_THEME` weights the slot's theme ×3.
 * - **Caps** (`AFFIX_CAPS`) clamp the equipped sum, in `equipmentStats` (data/items.ts).
 *
 * Rolls take the World's injected `Rng` only (no `Math.random`), so a seed always rolls the same item.
 */
import type { Rng } from '../rng';
import type { EquipSlot, ItemStats, Rarity } from './items';
import type { WeaponKind } from './weapons';

/** The stats an affix adds to (all of them are `ItemStats` keys). */
export type AffixStat = keyof ItemStats;

/** The affix pools (for the tooltip's grouping and the design table). */
export type AffixPool = 'offence' | 'internal' | 'school' | 'triangle' | 'status' | 'defence' | 'qinggong' | 'element';

export interface AffixDef {
  readonly id: string;
  /** Thai label, as the tooltip reads it. */
  readonly label: string;
  readonly pool: AffixPool;
  /**
   * The stat it adds to. `artDmg` and `schoolDmg` add to one art's or one school's damage instead (their target is
   * the roll's `arg`), so they have no stat.
   */
  readonly stat?: AffixStat;
  /** The value band at ilv 1 and at ilv 60. */
  readonly at1: readonly [number, number];
  readonly at60: readonly [number, number];
  /** Decimals the value keeps. */
  readonly dp: number;
  /** One affix per group per item (defaults to the id). */
  readonly group?: string;
  /** Slots it may roll on. */
  readonly slots: readonly EquipSlot[];
  /** Weapons only: the kinds it may roll on (any when absent). */
  readonly kinds?: readonly WeaponKind[];
  /** Relative weight before the slot theme (1 when absent). */
  readonly w?: number;
  /** Shown as a percentage. */
  readonly pct?: boolean;
}

/** One affix on one item. */
export interface AffixRoll {
  readonly id: string;
  /** The value added (already scaled by the item's budget share). */
  readonly v: number;
  /** Quality inside the band, 0–100. */
  readonly q: number;
  /** `artDmg`: the art id; `schoolDmg`: the school id. */
  readonly arg?: string;
  /** Kept through a ล้างค่า (R3). */
  readonly locked?: boolean;
  /** The base's own signature (the demon sword's art, a manual's art): outside the budget, never rerolled. */
  readonly imp?: boolean;
}

// Slot groups the table below uses.
const ARMOUR: readonly EquipSlot[] = ['head', 'body', 'legs', 'feet'];
const ACC: readonly EquipSlot[] = ['treasure', 'ring', 'pendant'];
const ART_SLOTS: readonly EquipSlot[] = ['weapon', 'manual', 'treasure', 'pendant'];
const STRIKE: readonly EquipSlot[] = ['weapon', 'ring', 'treasure', 'hidden'];

const list: AffixDef[] = [
  // ── Offence (ภายนอก) ──
  { id: 'atk', label: 'พลังโจมตี', pool: 'offence', stat: 'atk', at1: [1, 3], at60: [12, 30], dp: 1, slots: ['weapon', 'ring', 'treasure', 'hidden', 'head'] },
  { id: 'hit', label: 'แม่นยำ', pool: 'offence', stat: 'hit', at1: [1, 3], at60: [8, 20], dp: 0, slots: ['weapon', 'ring', 'head', 'treasure', 'hidden'] },
  { id: 'crit', label: 'ติดคริ', pool: 'offence', stat: 'crit', at1: [0.01, 0.02], at60: [0.03, 0.06], dp: 3, pct: true, slots: [...STRIKE, 'head'] },
  { id: 'critDmg', label: 'แรงคริ', pool: 'offence', stat: 'critDmg', at1: [0.03, 0.06], at60: [0.1, 0.2], dp: 3, pct: true, slots: STRIKE },
  { id: 'lifesteal', label: 'ดูดเลือด', pool: 'offence', stat: 'lifesteal', at1: [0.005, 0.01], at60: [0.015, 0.03], dp: 3, pct: true, slots: ['weapon', 'ring'], w: 0.6 },
  { id: 'defPierce', label: 'เจาะเกราะ', pool: 'offence', stat: 'defPierce', at1: [0.01, 0.03], at60: [0.04, 0.08], dp: 3, pct: true, slots: ['weapon', 'ring', 'hidden'] },
  // ── Internal arts (ภายใน) ──
  { id: 'magic', label: 'พลังโจมตีภายใน', pool: 'internal', stat: 'magic', at1: [1, 3], at60: [12, 30], dp: 1, slots: ['weapon', 'ring', 'pendant', 'treasure', 'manual'] },
  { id: 'magicHit', label: 'แม่นยำภายใน', pool: 'internal', stat: 'magicHit', at1: [1, 3], at60: [8, 20], dp: 0, slots: ['weapon', 'ring', 'head', 'pendant', 'treasure'] },
  { id: 'magicCrit', label: 'ติดคริภายใน', pool: 'internal', stat: 'magicCrit', at1: [0.01, 0.02], at60: [0.03, 0.06], dp: 3, pct: true, slots: ['weapon', 'ring', 'pendant', 'treasure'] },
  { id: 'maxQi', label: 'ลมปราณสูงสุด', pool: 'internal', stat: 'maxQi', at1: [5, 10], at60: [40, 80], dp: 0, slots: ['head', 'body', 'pendant', 'treasure', 'manual'] },
  { id: 'qiRegen', label: 'ฟื้นลมปราณ', pool: 'internal', stat: 'qiRegen', at1: [0.1, 0.2], at60: [0.6, 1.2], dp: 2, slots: ['head', 'pendant', 'treasure', 'weapon', 'manual'] },
  { id: 'qiCostPct', label: 'ลดลมปราณที่ใช้', pool: 'internal', stat: 'qiCostPct', at1: [0.01, 0.02], at60: [0.03, 0.06], dp: 3, pct: true, slots: ['weapon', 'manual', 'pendant', 'treasure'] },
  { id: 'cdPct', label: 'ลดคูลดาวน์', pool: 'internal', stat: 'cdPct', at1: [0.01, 0.02], at60: [0.02, 0.04], dp: 3, pct: true, slots: ['manual', 'pendant', 'treasure', 'head'], w: 0.6 },
  { id: 'rageGain', label: 'ความโกรธที่ได้', pool: 'internal', stat: 'rageGain', at1: [0.02, 0.05], at60: [0.06, 0.12], dp: 3, pct: true, slots: ['weapon', 'body', 'ring', 'manual'] },
  // ── School / art ──
  { id: 'schoolDmg', label: 'ความเสียหายวิชาสำนัก', pool: 'school', at1: [0.02, 0.04], at60: [0.06, 0.12], dp: 3, pct: true, slots: ART_SLOTS },
  { id: 'artDmg', label: 'ความเสียหายวิชา', pool: 'school', at1: [0.03, 0.05], at60: [0.08, 0.15], dp: 3, pct: true, slots: ART_SLOTS },
  // ── Triangle ──
  { id: 'guardBreak', label: 'แรงทำลายการ์ด', pool: 'triangle', stat: 'guardBreak', at1: [0.02, 0.05], at60: [0.08, 0.15], dp: 3, pct: true, slots: ['weapon', 'ring', 'treasure'] },
  { id: 'guardHp', label: 'ความทนการ์ด', pool: 'triangle', stat: 'guardHp', at1: [0.02, 0.05], at60: [0.08, 0.15], dp: 3, pct: true, slots: ['body', 'legs', 'head', 'pendant', 'treasure'] },
  { id: 'breakWindup', label: 'ลดเวลาชาร์จท่าทำลาย', pool: 'triangle', stat: 'breakWindup', at1: [0.01, 0.02], at60: [0.02, 0.04], dp: 3, pct: true, slots: ['weapon', 'manual', 'treasure'], w: 0.6 },
  { id: 'counterDmg', label: 'ความเสียหายสวนกลับ', pool: 'triangle', stat: 'counterDmg', at1: [0.03, 0.06], at60: [0.1, 0.2], dp: 3, pct: true, slots: ['weapon', 'body', 'ring', 'treasure'] },
  // ── Status ──
  { id: 'poisonChance', label: 'โอกาสพิษ', pool: 'status', stat: 'poisonChance', at1: [0.01, 0.03], at60: [0.04, 0.08], dp: 3, pct: true, slots: STRIKE },
  { id: 'bleedChance', label: 'โอกาสเลือดไหล', pool: 'status', stat: 'bleedChance', at1: [0.01, 0.03], at60: [0.04, 0.08], dp: 3, pct: true, slots: STRIKE },
  { id: 'stunPower', label: 'แรงมึนงง', pool: 'status', stat: 'stunPower', at1: [0.02, 0.05], at60: [0.08, 0.15], dp: 3, pct: true, slots: ['weapon', 'ring', 'treasure', 'hidden'] },
  { id: 'poisonRes', label: 'ต้านพิษ', pool: 'status', stat: 'poisonRes', at1: [0.02, 0.04], at60: [0.06, 0.12], dp: 3, pct: true, slots: [...ARMOUR, ...ACC] },
  { id: 'bleedRes', label: 'ต้านเลือดไหล', pool: 'status', stat: 'bleedRes', at1: [0.02, 0.04], at60: [0.06, 0.12], dp: 3, pct: true, slots: [...ARMOUR, ...ACC] },
  { id: 'toughness', label: 'ต้านมึนงง', pool: 'status', stat: 'toughness', at1: [1, 3], at60: [6, 15], dp: 1, slots: [...ARMOUR, ...ACC] },
  // ── Defence ──
  { id: 'maxHp', label: 'พลังชีวิตสูงสุด', pool: 'defence', stat: 'maxHp', at1: [8, 20], at60: [80, 200], dp: 0, slots: [...ARMOUR, ...ACC] },
  { id: 'def', label: 'ป้องกัน', pool: 'defence', stat: 'def', at1: [1, 3], at60: [8, 20], dp: 0, slots: [...ARMOUR, 'treasure', 'pendant'] },
  { id: 'magicDef', label: 'ป้องกันภายใน', pool: 'defence', stat: 'magicDef', at1: [1, 3], at60: [8, 20], dp: 0, slots: [...ARMOUR, 'treasure', 'pendant'] },
  { id: 'dodge', label: 'หลบหลีก', pool: 'defence', stat: 'dodge', at1: [1, 3], at60: [6, 15], dp: 0, slots: ['feet', 'legs', 'head', 'ring', 'treasure'] },
  { id: 'hpRegen', label: 'ฟื้นพลังชีวิต', pool: 'defence', stat: 'hpRegen', at1: [0.2, 0.5], at60: [1.5, 3], dp: 2, slots: ['body', 'legs', 'pendant', 'treasure'] },
  { id: 'critRes', label: 'ลดคริที่โดน', pool: 'defence', stat: 'critRes', at1: [0.01, 0.02], at60: [0.03, 0.06], dp: 3, pct: true, slots: [...ARMOUR, 'pendant'] },
  // ── Qinggong (วิชาตัวเบา): feet, legs and treasures only ──
  { id: 'speed', label: 'ความเร็ว', pool: 'qinggong', stat: 'speed', at1: [0.01, 0.02], at60: [0.03, 0.06], dp: 3, pct: true, slots: ['feet', 'legs', 'treasure'] },
  { id: 'maxStamina', label: 'พลังกายสูงสุด', pool: 'qinggong', stat: 'maxStamina', at1: [5, 10], at60: [30, 60], dp: 0, slots: ['feet', 'legs', 'treasure'] },
  { id: 'flyCostPct', label: 'ลดพลังกายตอนเหาะ', pool: 'qinggong', stat: 'flyCostPct', at1: [0.02, 0.04], at60: [0.05, 0.1], dp: 3, pct: true, slots: ['feet', 'legs', 'treasure'] },
  { id: 'glideLen', label: 'ระยะร่อน', pool: 'qinggong', stat: 'glideLen', at1: [0.03, 0.06], at60: [0.08, 0.15], dp: 3, pct: true, slots: ['feet', 'legs', 'treasure'] },
  { id: 'fallRes', label: 'ลดความเสียหายตก', pool: 'qinggong', stat: 'fallRes', at1: [0.05, 0.1], at60: [0.15, 0.3], dp: 3, pct: true, slots: ['feet', 'legs', 'treasure'] },
  // ── Elements (data/forge.ts elements) ──
  { id: 'resFire', label: 'ต้านธาตุไฟ', pool: 'element', stat: 'resFire', group: 'res-el', at1: [0.02, 0.04], at60: [0.06, 0.12], dp: 3, pct: true, slots: [...ARMOUR, 'pendant', 'treasure'] },
  { id: 'resWater', label: 'ต้านธาตุน้ำ', pool: 'element', stat: 'resWater', group: 'res-el', at1: [0.02, 0.04], at60: [0.06, 0.12], dp: 3, pct: true, slots: [...ARMOUR, 'pendant', 'treasure'] },
  { id: 'resEarth', label: 'ต้านธาตุดิน', pool: 'element', stat: 'resEarth', group: 'res-el', at1: [0.02, 0.04], at60: [0.06, 0.12], dp: 3, pct: true, slots: [...ARMOUR, 'pendant', 'treasure'] },
  { id: 'resWind', label: 'ต้านธาตุลม', pool: 'element', stat: 'resWind', group: 'res-el', at1: [0.02, 0.04], at60: [0.06, 0.12], dp: 3, pct: true, slots: [...ARMOUR, 'pendant', 'treasure'] },
  { id: 'resPoison', label: 'ต้านธาตุพิษ', pool: 'element', stat: 'resPoison', group: 'res-el', at1: [0.02, 0.04], at60: [0.06, 0.12], dp: 3, pct: true, slots: [...ARMOUR, 'pendant', 'treasure'] },
];

export const AFFIXES: Readonly<Record<string, AffixDef>> = Object.fromEntries(list.map((a) => [a.id, a]));
export const AFFIX_LIST: readonly AffixDef[] = list;

/** The affix that carries each stat (the migrator turns an old rolled stat into it). */
export const AFFIX_OF_STAT: Readonly<Partial<Record<AffixStat, string>>> = Object.fromEntries(
  list.filter((a) => a.stat).map((a) => [a.stat!, a.id]),
);

/** Each slot's theme: these affixes weigh ×3 there (ITEMS_V2 §1.2 implicit column). */
export const SLOT_THEME: Readonly<Record<EquipSlot, readonly string[]>> = {
  weapon: ['atk', 'magic', 'crit', 'artDmg', 'schoolDmg', 'guardBreak'],
  head: ['magicDef', 'maxHp', 'maxQi'],
  body: ['def', 'maxHp', 'guardHp'],
  legs: ['def', 'maxStamina', 'maxHp'],
  feet: ['dodge', 'speed', 'flyCostPct', 'glideLen', 'fallRes'],
  hidden: ['poisonChance', 'bleedChance', 'stunPower'],
  treasure: [],
  manual: ['artDmg', 'schoolDmg', 'cdPct', 'qiCostPct'],
  ring: ['magicHit', 'hit'],
  pendant: ['maxQi', 'qiRegen'],
};
const THEME_W = 3;

/** Item level range. */
export const ILV_MIN = 1;
export const ILV_MAX = 60;

/** Affixes per rarity: [min, max] (ธรรมดา rolls 0 or 1). */
export const AFFIX_COUNT: Readonly<Record<Rarity, readonly [number, number]>> = {
  common: [0, 1],
  fine: [2, 2],
  rare: [3, 3],
  epic: [4, 4],
  legend: [5, 5],
  mythic: [6, 6],
};

/** The budget multiplier per rarity (§4.1: 1 / 1.1 / 1.2 / 1.3 / 1.4 / 1.5). */
export const RARITY_MUL: Readonly<Record<Rarity, number>> = {
  common: 1,
  fine: 1.1,
  rare: 1.2,
  epic: 1.3,
  legend: 1.4,
  mythic: 1.5,
};

/**
 * The power budget, in affix units (one unit = one affix at the top of its band at that ilv): `B × rarityMul`. ilv
 * scales the bands themselves, so B is the same at every level. B = 2.5 makes a 3-affix หายาก exactly full
 * (3 units) and a 6-affix ไร้เทียมทาน 3.75 units over 6 lines.
 */
export const BUDGET_UNITS = 2.5;
export const itemBudget = (rarity: Rarity): number => BUDGET_UNITS * RARITY_MUL[rarity];

/** Caps on the equipped sum (§4.1), applied in `equipmentStats`. */
export const AFFIX_CAPS: Readonly<Partial<Record<AffixStat, number>>> = {
  cdPct: 0.15,
  breakWindup: 0.1,
  crit: 0.4,
  magicCrit: 0.4,
  lifesteal: 0.08,
  poisonRes: 0.5,
  bleedRes: 0.5,
  qiCostPct: 0.3,
  flyCostPct: 0.5,
  fallRes: 0.8,
  critRes: 0.3,
  resFire: 0.5,
  resWater: 0.5,
  resEarth: 0.5,
  resWind: 0.5,
  resPoison: 0.5,
};

export const clampIlv = (ilv: number): number =>
  Math.max(ILV_MIN, Math.min(ILV_MAX, Math.floor(Number.isFinite(ilv) ? ilv : ILV_MIN)));

/** An affix's value band at an item level (it steps every 10 levels). */
export function affixBand(a: AffixDef, ilv: number): [number, number] {
  const step = Math.min(5, Math.floor((clampIlv(ilv) - 1) / 10));
  const t = step / 5;
  return [a.at1[0] + (a.at60[0] - a.at1[0]) * t, a.at1[1] + (a.at60[1] - a.at1[1]) * t];
}

/** Rounds down to `dp` decimals (so a roll never goes past its budget). */
const floorTo = (x: number, dp: number): number => {
  const f = 10 ** dp;
  return Math.floor(x * f + 1e-9) / f;
};

/** The value of an affix at a quality, scaled by the item's budget share. */
export function affixValue(a: AffixDef, ilv: number, q: number, share = 1): number {
  const [lo, hi] = affixBand(a, ilv);
  const qq = Math.max(0, Math.min(100, q)) / 100;
  return floorTo((lo + (hi - lo) * qq) * share, a.dp);
}

/** What an affix costs of an item's budget: its value over its band's top at that ilv. */
export function affixCost(r: AffixRoll, ilv: number): number {
  const a = AFFIXES[r.id];
  if (!a || r.imp) return 0;
  return Math.abs(r.v) / affixBand(a, ilv)[1];
}

/** Each rolled affix's share of the top of its band, for an item of this rarity rolling `n` affixes. */
export const budgetShare = (rarity: Rarity, n: number): number => (n <= 0 ? 1 : Math.min(1, itemBudget(rarity) / n));

/** What `rollAffixes` needs to know of the base. */
export interface RollBase {
  readonly slot: EquipSlot;
  readonly kind?: WeaponKind;
}

export interface RollOpts {
  /** Arts `artDmg` may name (none: `artDmg` does not roll). */
  readonly arts?: readonly string[];
  /** Schools `schoolDmg` may name (none: it does not roll). */
  readonly schools?: readonly string[];
  /** Affixes the base carries outside the budget (a signature); their groups do not roll again. */
  readonly implicit?: readonly AffixRoll[];
  /** Overrides the rarity's count. */
  readonly count?: number;
}

/** The affixes allowed on a base. */
export function affixPool(base: RollBase, opts: RollOpts = {}): AffixDef[] {
  return list.filter(
    (a) =>
      a.slots.includes(base.slot) &&
      (!a.kinds || (base.kind !== undefined && a.kinds.includes(base.kind))) &&
      (a.id !== 'artDmg' || !!opts.arts?.length) &&
      (a.id !== 'schoolDmg' || !!opts.schools?.length),
  );
}

/**
 * Rolls a base's random affixes at an item level and rarity, through `rng` only. Draws, in order: the count (ธรรมดา
 * only), then per affix one pick, one target (artDmg / schoolDmg only) and one quality.
 */
export function rollAffixes(base: RollBase, ilv: number, rarity: Rarity, rng: Rng, opts: RollOpts = {}): AffixRoll[] {
  const lv = clampIlv(ilv);
  const [lo, hi] = AFFIX_COUNT[rarity];
  const want = opts.count ?? (lo === hi ? lo : lo + Math.floor(rng() * (hi - lo + 1)));
  const taken = new Set((opts.implicit ?? []).map((r) => AFFIXES[r.id]?.group ?? r.id));
  let pool = affixPool(base, opts).filter((a) => !taken.has(a.group ?? a.id));
  const theme = SLOT_THEME[base.slot];
  const n = Math.min(want, new Set(pool.map((a) => a.group ?? a.id)).size);
  const share = budgetShare(rarity, n);
  const out: AffixRoll[] = [];
  for (let k = 0; k < n && pool.length; k++) {
    const weights = pool.map((a) => (a.w ?? 1) * (theme.includes(a.id) ? THEME_W : 1));
    let r = rng() * weights.reduce((s, x) => s + x, 0);
    let i = 0;
    while (i < pool.length - 1 && (r -= weights[i]!) >= 0) i++;
    const a = pool[i]!;
    const group = a.group ?? a.id;
    pool = pool.filter((x) => (x.group ?? x.id) !== group);
    const args = a.id === 'artDmg' ? opts.arts : a.id === 'schoolDmg' ? opts.schools : undefined;
    const arg = args ? args[Math.floor(rng() * args.length)] : undefined;
    const q = Math.floor(rng() * 101);
    out.push({ id: a.id, v: affixValue(a, lv, q, share), q, ...(arg ? { arg } : {}) });
  }
  return out;
}

/** The affix line a tooltip shows (พลังโจมตี +12.5, ติดคริ +3.2 %). */
export function affixText(r: AffixRoll): string {
  const a = AFFIXES[r.id];
  if (!a) return r.id;
  const v = a.pct ? `${Math.round(r.v * 1000) / 10} %` : `${r.v}`;
  return `${a.label}${r.arg ? ` (${r.arg})` : ''} +${v}`;
}
